import { Gauge, ChevronRight } from 'lucide-react';
import { useChargerUtilization } from '@/hooks/useChargerUtilization';
import { formatActiveHours } from './_lib';
import styles from '../DashboardPage.module.css';

/**
 * Per-charger utilization widget. Session-based (NOT activity-
 * based) — a charger sitting idle but pinging heartbeats is
 * correctly NOT counted. The activity measure lives on the
 * Charger Uptime widget.
 *
 * Color tiers convey demand at a glance:
 *   - >80%: high demand (accent green) — capacity-planning
 *           signal; consider adding a stall
 *   - 30-80%: healthy (neutral)
 *   - <30%: under-utilized (warn amber) — action item; why is
 *           this charger sitting idle?
 *
 * Shows EVERY active charger in scope, including ones with 0%.
 * "This charger had ZERO sessions in 7d" is a load-bearing
 * signal — hiding it would mask the action item.
 *
 * Sort: utilization desc. Most-used chargers float to the top,
 * under-utilized ones at the bottom — operators looking for
 * capacity issues read top-down, operators looking for under-
 * used chargers scroll to the bottom.
 */
export default function ChargerUtilizationWidget({ onNavigate }: { onNavigate?: (to: string) => void }) {
  const q = useChargerUtilization();
  const data = q.data;

  return (
    <div className={styles.chartBody}>
      <div className={styles.chartHeader}>
        <div className={styles.chartIcon}><Gauge size={14} /></div>
        <p className={styles.chartTitle}>Charger utilization · 7d</p>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><Gauge size={18} /></span>
          <p>
            {data.is_scoped
              ? 'No chargers in your scope.'
              : 'No chargers in this tenant.'}
          </p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className={styles.utilList}>
          {data.rows.map((row) => {
            const tier = utilizationTier(row.utilization_percent);
            return (
              <a
                key={row.charger_id}
                href={`/chargers/${row.charger_id}`}
                onClick={(e) => {
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate(`/chargers/${row.charger_id}`);
                  }
                }}
                className={styles.utilRow}
              >
                <div className={styles.utilBody}>
                  <span className={styles.utilTitleLine}>
                    {row.charger_identity}
                    {row.location_name ? ` · ${row.location_name}` : ''}
                  </span>
                  <span className={styles.utilMetaLine}>
                    {row.session_count} session{row.session_count === 1 ? '' : 's'}
                    {' · '}
                    {formatActiveHours(row.total_active_minutes)}
                  </span>
                  <div className={styles.utilBarTrack}>
                    <div
                      className={`${styles.utilBarFill} ${utilizationBarClass(tier)}`}
                      style={{ width: `${row.utilization_percent.toFixed(1)}%` }}
                    />
                  </div>
                </div>
                <span className={`${styles.utilPercent} ${utilizationPercentClass(tier)}`}>
                  {Math.round(row.utilization_percent)}%
                </span>
                <ChevronRight size={14} className={styles.topRevChevron} />
              </a>
            );
          })}
        </div>
      )}

      {data?.session_basis === 'owned_chargers' && (
        <p className={styles.chartDesc} style={{ maxWidth: 'none', paddingTop: 8 }}>
          Fleet view: every session on your fleet's chargers, whoever paid.
        </p>
      )}
    </div>
  );
}

type UtilizationTier = 'high' | 'healthy' | 'low';

function utilizationTier(pct: number): UtilizationTier {
  if (pct >= 80) return 'high';
  if (pct >= 30) return 'healthy';
  return 'low';
}

function utilizationBarClass(tier: UtilizationTier): string {
  if (tier === 'high') return styles.utilBarFillHigh ?? '';
  if (tier === 'low') return styles.utilBarFillLow ?? '';
  return styles.utilBarFillHealthy ?? '';
}

function utilizationPercentClass(tier: UtilizationTier): string {
  if (tier === 'high') return styles.utilPercentHigh ?? '';
  if (tier === 'low') return styles.utilPercentLow ?? '';
  return styles.utilPercentHealthy ?? '';
}
